import { useParams } from 'react-router-dom'

import { APP } from 'src/Configs/App'
import { rtkApi } from 'src/Services/rtkApi'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useAppSelector } from 'src/Hooks/redux'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { selectManualMatchingBudget } from 'src/Redux/selectedCuratorsSlice'
import { getIsManualMatchingCampaign } from 'src/Hooks/Experiments/useManualMatchingExperiment'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

import { getIsUserReferral } from '../auth-process/userSlice/selectors'
import { getHasPaidCampaign, usePaidCampaignsCountQuery } from '../metadataApi'

interface BudgetRangeResponse {
  minBudget?: number
  maxBudget: number
  curatorsCount?: number
  playlistsCount?: number
  absoluteMinBudgetForUser: number
  minBudgetForReferralUser: number
}

interface BudgetRange {
  min?: number
  max: number
  absoluteCuratorsCount?: number
  absoluteMinBudgetForUser: number
  minBudgetForReferralUser: number
}

interface UseBudgetRange {
  isError: boolean
  isFetching: boolean
  isLowReachError: boolean
  data?: BudgetRange
}

const spotifyBudgetRangeApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getBudgetRange: builder.query<BudgetRange, string | undefined>({
      query: (campaignId) =>
        `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${campaignId}/budget-range`,
      transformResponse(response: BudgetRangeResponse) {
        return {
          min: response.minBudget,
          max: response?.maxBudget,
          absoluteCuratorsCount: response?.curatorsCount,
          absoluteMinBudgetForUser: response?.absoluteMinBudgetForUser || 0,
          minBudgetForReferralUser: response?.minBudgetForReferralUser || 0,
        }
      },
    }),
  }),
  overrideExisting: false,
})

export const { useGetBudgetRangeQuery, useLazyGetBudgetRangeQuery } =
  spotifyBudgetRangeApi

const applyReferralMin = (
  data: BudgetRange,
  isReferralFirstCampaign: boolean,
): BudgetRange => {
  const { min, minBudgetForReferralUser } = data

  if (
    !isReferralFirstCampaign ||
    typeof min !== 'number' ||
    !minBudgetForReferralUser
  ) {
    return data
  }

  return {
    ...data,
    min: minBudgetForReferralUser,
  }
}

const getIsLowReachError = (
  isManualMatching: boolean,
  isError: boolean,
  data?: BudgetRange,
  manualMatchingBudget?: number | null,
): boolean => {
  if (isManualMatching) {
    return (
      typeof manualMatchingBudget === 'number' &&
      typeof data?.absoluteMinBudgetForUser === 'number' &&
      manualMatchingBudget < data.absoluteMinBudgetForUser
    )
  }

  if (!data) {
    return false
  }

  return data.max < data.absoluteMinBudgetForUser && !isError
}

const getSkipBudgetRange = ({
  campaignId,
  campaignIdFromStatus,
  paymentStatus,
  hasGenres,
  hasSelectedLanguagesWithoutAny,
}: {
  campaignId?: string
  campaignIdFromStatus?: string | number
  paymentStatus: string | null
  hasGenres: boolean
  hasSelectedLanguagesWithoutAny: boolean
}): boolean => {
  const notValidCampaignId =
    !campaignId || String(campaignIdFromStatus) !== String(campaignId)

  return (
    notValidCampaignId ||
    paymentStatus === 'success' ||
    !hasGenres ||
    !hasSelectedLanguagesWithoutAny
  )
}

const getIsReferralFirstCampaign = (
  isUserReferral: boolean,
  hasPaidCampaign: boolean,
  isManualMatching: boolean,
): boolean => isUserReferral && !hasPaidCampaign && !isManualMatching

const getReferralUserBudgetError = (
  isReferralFirstCampaign: boolean,
  data?: BudgetRange,
): boolean => isReferralFirstCampaign && (data?.max ?? 0) < (data?.min ?? 0)

export const useBudgetRange = (): UseBudgetRange => {
  const { number: campaignId } = useParams()
  const { search } = useCustomSearchParams()

  const {
    campaignDetails: campaign,
    genresList,
    hasSelectedLanguagesWithoutAny,
    matchingType,
  } = useSpotifyCampaignStatus()

  const manualMatchingBudget = useAppSelector(selectManualMatchingBudget)
  const isUserReferral = useAppSelector(getIsUserReferral)

  const isManualMatching = getIsManualMatchingCampaign(matchingType)

  const { data: paidCampaignsCountData } = usePaidCampaignsCountQuery(
    CampaignPlatformTypeEnum.SPOTIFY,
    { skip: !isUserReferral },
  )

  const hasPaidCampaign = getHasPaidCampaign(paidCampaignsCountData)

  const skipBudgetRange = getSkipBudgetRange({
    campaignId,
    hasSelectedLanguagesWithoutAny,
    campaignIdFromStatus: campaign?.id,
    paymentStatus: search.get('paymentStatus'),
    hasGenres: Boolean(genresList.length),
  })

  const result = useGetBudgetRangeQuery(campaignId, {
    skip: skipBudgetRange,
  })

  const isReferralFirstCampaign = getIsReferralFirstCampaign(
    isUserReferral,
    hasPaidCampaign,
    isManualMatching,
  )

  const adjustedData = result.data
    ? applyReferralMin(result.data, isReferralFirstCampaign)
    : void 0

  const referralUserBudgetError = getReferralUserBudgetError(
    isReferralFirstCampaign,
    adjustedData,
  )

  const isError =
    adjustedData?.absoluteCuratorsCount === 0 || referralUserBudgetError

  const isLowReachError = getIsLowReachError(
    isManualMatching,
    isError,
    adjustedData,
    manualMatchingBudget,
  )

  return {
    isLowReachError,
    isError,
    isFetching: result.isFetching,
    data: adjustedData,
  }
}
